import React, { FC, useState } from "react";
import { MatterRelationEditor } from "../matter-relation-editor";
import { VpdList } from "../../vpd-list";
import { UpliftRow } from "../../uplifts";
import { Uplift, LibraryUplifts, DeepPartial } from "../../../data-types";
import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableItem, DraggableHandle } from "../../draggable-list";

type Props = {
  uplifts: Uplift[] | null;
  onUpdate: (discount: DeepPartial<Uplift>) => void;
  onDelete: (id: number) => void;
  libraryUplifts: LibraryUplifts;
  onReorder: (activeId: number, overId: number) => void;
  onAutoFocus: () => void;
  currencySymbol: string;
};
export const MatterUpliftsEditor: FC<Props> = ({
  uplifts,
  onDelete,
  onUpdate: onUpdateProp,
  libraryUplifts,
  onReorder,
  onAutoFocus,
  currencySymbol,
}) => {
  const onUpdate = (updatedUplift: DeepPartial<Uplift>) => {
    onUpdateProp(updatedUplift);
  };

  const sensors = useSensors(useSensor(PointerSensor));
  const [activeUplift, setActiveUplift] = useState<Uplift | null>(null);

  return (
    <MatterRelationEditor
      workspaceContent={
        <DndContext
          onDragStart={({ active }) => {
            setActiveUplift(uplifts?.find((p) => p.id.toString() === active.id)! ?? []);
          }}
          onDragEnd={({ active, over }) =>
            over && onReorder(parseInt(active.id), parseInt(over.id))
          }
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={uplifts?.map((p) => p.id.toString()) ?? []}
            strategy={verticalListSortingStrategy}
          >
            <VpdList
              itemKeyProperty="id"
              itemTextProperty="reason"
              loading={false}
              split={false}
              items={uplifts ?? []}
              renderItem={(item: Uplift) => {
                const isAutoFocused = item?.autoFocus ?? false;
                return (
                  <SortableItem id={item.id.toString()} key={item.id}>
                    {({ attributes, listeners }) => (
                      <VpdList.Item
                        key={item.id.toString()}
                        content={
                          <UpliftRow
                            currencySymbol={currencySymbol}
                            autoFocus={isAutoFocused}
                            onAutoFocus={onAutoFocus}
                            uplift={item}
                            libraryUplifts={libraryUplifts}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                          />
                        }
                        contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                      />
                    )}
                  </SortableItem>
                );
              }}
              listStyle="primary"
              labels={{
                emptyText: "No items available.",
                loadingText: "Loading items...",
              }}
            />
          </SortableContext>
          <DragOverlay>
            {activeUplift ? (
              <VpdList
                itemKeyProperty="id"
                itemTextProperty="reason"
                loading={false}
                split={false}
                items={[activeUplift]}
                renderItem={(item: Uplift) => (
                  <VpdList.Item
                    key={item.id.toString()}
                    content={
                      <UpliftRow
                        currencySymbol={currencySymbol}
                        uplift={item}
                        libraryUplifts={libraryUplifts}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                      />
                    }
                    contentPrefix={<DraggableHandle />}
                  />
                )}
                listStyle="primary"
                labels={{
                  emptyText: "No items available.",
                  loadingText: "Loading items...",
                }}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      }
      addNew={undefined}
      showAddNew={false}
    />
  );
};
